import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useRef } from 'react';
import { z } from 'zod';
import { trackedWalletsQuery } from '@/features/tracked/tracked-api';
import { TrackedScreen } from '@/features/tracked/tracked-screen';

export const Route = createFileRoute('/_app/tracked')({
  validateSearch: z.object({
    /** The wallet whose sheet is open. */
    id: z
      .string()
      .regex(/^[a-f0-9]{24}$/)
      .optional()
      .catch(undefined),
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(trackedWalletsQuery),
  component: TrackedRoute,
});

function TrackedRoute() {
  const { id } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const { data: wallets } = useSuspenseQuery(trackedWalletsQuery);
  // Opening a sheet pushes a history entry, so Back closes it. Closing it from inside (Escape, the
  // close button) steps back over that entry instead of stacking another one; a sheet opened from
  // a link or a reload has no entry of ours behind it, so it's replaced.
  const pushed = useRef(false);

  const onOpen = useCallback(
    (next: string) => {
      pushed.current = true;
      void navigate({ search: { id: next } });
    },
    [navigate],
  );
  const onClose = useCallback(() => {
    if (pushed.current) {
      pushed.current = false;
      router.history.back();
    } else {
      void navigate({ search: {}, replace: true });
    }
  }, [navigate, router]);

  return <TrackedScreen wallets={wallets} selectedId={id} onOpen={onOpen} onClose={onClose} />;
}
